
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { CircleDecor } from '@/components/ui/visual-elements';
import { toast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TestHistoryItem {
  id: string;
  test_type: {
    name: string;
    description: string;
  };
  created_at: string;
  result_id: string;
}

export default function TestHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState<TestHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');

  useEffect(() => {
    const fetchTests = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('personality_tests')
          .select(`
            id,
            created_at,
            result_reference_id: result_reference_id,
            test_type: test_type_id (
              name,
              description
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        if (data) {
          setTests(data.map(item => ({
            id: item.id,
            test_type: item.test_type as { name: string; description: string },
            created_at: item.created_at || '',
            result_id: item.result_reference_id
          })));
        }
        
      } catch (error: any) {
        console.error('Erro ao carregar histórico de testes:', error);
        toast({
          title: "Erro ao carregar histórico",
          description: error.message || "Não foi possível carregar seu histórico de testes.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchTests();
  }, [user]);

  const filteredTests = activeTab === 'all' 
    ? tests 
    : tests.filter(test => test.test_type.name === activeTab);

  const getTestRoute = (testType: string, resultId: string) => {
    switch (testType) {
      case 'big-five':
        return `/personalidade`;
      case 'young-schemas':
        return `/young-schemas`;
      default:
        return `/personalidade`;
    }
  };

  const getTestIcon = (testType: string) => {
    switch (testType) {
      case 'big-five':
        return '🌟';
      case 'young-schemas':
        return '🧠';
      default:
        return '📊';
    }
  };

  const getTestTitle = (testType: string) => {
    switch (testType) {
      case 'big-five':
        return 'Big Five (OCEAN)';
      case 'young-schemas':
        return 'Esquemas de Young';
      default:
        return testType;
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto p-6 relative">
        <CircleDecor className="absolute -top-10 -right-10 z-0 opacity-10" size="lg" />
        <CircleDecor className="absolute top-40 -left-10 z-0 opacity-10" variant="secondary" />
        
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold mb-2">Histórico de Testes</h1>
          <p className="text-sm text-gray-500 mb-6">
            Veja seu histórico de testes de personalidade e acesse os resultados anteriores.
          </p>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList>
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="big-five">Big Five</TabsTrigger>
              <TabsTrigger value="young-schemas">Esquemas de Young</TabsTrigger>
            </TabsList>
          </Tabs>
          
          {loading ? (
            <div className="flex justify-center my-12">
              <p>Carregando seu histórico...</p>
            </div>
          ) : filteredTests.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <p className="text-gray-500 mb-4">
                  {activeTab === 'all' 
                    ? 'Você ainda não realizou nenhum teste de personalidade.' 
                    : `Você ainda não realizou o teste de ${activeTab === 'big-five' ? 'Big Five' : 'Esquemas de Young'}.`}
                </p>
                <Button onClick={() => navigate('/personalidade')}>
                  Fazer um teste agora
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredTests.map((test) => (
                <Card key={test.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{getTestIcon(test.test_type.name)}</span>
                        <CardTitle>{getTestTitle(test.test_type.name)}</CardTitle>
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatDistanceToNow(new Date(test.created_at), { 
                          addSuffix: true,
                          locale: ptBR
                        })}
                      </span>
                    </div>
                    <CardDescription>{test.test_type.description}</CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate(getTestRoute(test.test_type.name, test.result_id))}
                    >
                      Ver resultados
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
          
          <div className="mt-8 flex justify-center">
            <Button onClick={() => navigate('/personalidade')}>Realizar novo teste</Button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
